﻿////////////////////////////////////////////////////////////////////////////////////
// CONSTANTS
////////////////////////////////////////////////////////////////////////////////////
const SCROLL_TO_TOP_THRESHOLD = 450;
const STICKY_THRESHOLD = 150; // px from top before sticky hide/show behaviour kicks in
const WAYPOINT_OFFSET_PERCENTAGE = 0.6;
const SCRIBBLE_FADE_OPACITY_MIN = 0.2;
const SCROLL_THROTTLE_MS = 16; // ~60fps
const DEFAULT_SEGMENT_DELAY = 100;

////////////////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTIONS
////////////////////////////////////////////////////////////////////////////////////
function fadeIn(element, duration = 300) {
    element.style.display = 'block';
    element.style.opacity = '0';

    let start = null;
    const animate = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        element.style.opacity = Math.min(progress / duration, 1);

        if (progress < duration) {
            requestAnimationFrame(animate);
        }
    };
    requestAnimationFrame(animate);
}

function fadeOut(element, duration = 300) {
    let start = null;
    const initialOpacity = parseFloat(getComputedStyle(element).opacity) || 1;

    const animate = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        element.style.opacity = initialOpacity - (progress / duration);

        if (progress < duration) {
            requestAnimationFrame(animate);
        } else {
            element.style.display = 'none';
        }
    };
    requestAnimationFrame(animate);
}

function isSitefinityEditor() {
    return document.body.classList.contains("sfPageEditor");
}

function shouldEnableAnimations() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Scroll reveal
const reveals = document.querySelectorAll('.reveal');

// Apply initial styles to all reveal elements
reveals.forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(28px)';

    // Check for delay classes dynamically (reveal-delay-1, reveal-delay-2, reveal-delay-10, etc.)
    let delay = '0s';
    const delayClass = Array.from(el.classList).find(cls => cls.startsWith('reveal-delay-'));
    if (delayClass) {
        const delayNumber = parseInt(delayClass.replace('reveal-delay-', ''), 10);
        if (!isNaN(delayNumber)) {
            delay = `${delayNumber * 0.1}s`; // 0.1s per delay step
        }
    }

    el.style.transition = `opacity 0.7s ease ${delay}, transform 0.7s ease ${delay}`;
});

const observer = new IntersectionObserver((entries) => {
    entries.forEach(e => {
        if (e.isIntersecting) {
            e.target.style.opacity = '1';
            e.target.style.transform = 'translateY(0)';
            observer.unobserve(e.target);
        }
    });
}, { threshold: 0.1 });

reveals.forEach(r => observer.observe(r));

////////////////////////////////////////////////////////////////////////////////////
// GLOBAL VARS
////////////////////////////////////////////////////////////////////////////////////
window.ww = window.innerWidth;
window.wh = window.innerHeight;
window.dh = document.documentElement.scrollHeight;
window.hh = 0;
window.st = 0;
window.lastScrollTop = 0;
window.scrollDirection = "down";
window.selectedClassName = 'selected';
window.menuOpenClassName = 'open';
window.fadeInSpeed = 50;
window.fadeOutSpeed = 75;
window.navMouseOutDelay = 600;
window.mouseOutTimer = null;
window.is_responsive = false;
window.scrollProgress = 0;

// Cached DOM elements
const body = document.body;
const header = document.querySelector('#header'); // fixed: was "#wrapper > header"
const scrollNav = document.querySelector('.scrollToTop');
const isHome = body.classList.contains("homePage");

// Expose to window for compatibility
window.$header = header;
window.isHome = isHome;
window.$scrollNav = scrollNav;

function updateWindowDimensions() {
    window.ww = window.innerWidth;
    window.wh = window.innerHeight;
    window.dh = document.documentElement.scrollHeight;
    window.hh = header ? header.getBoundingClientRect().height : 0;
}

// Initialize on page load
if (isSitefinityEditor()) {
    body.classList.add("loaded");
}

updateWindowDimensions();

////////////////////////////////////////////////////////////////////////////////////
// SCROLL HANDLING
////////////////////////////////////////////////////////////////////////////////////
function refreshScrollData() {
    updateScrollProgress();
    window.st = window.pageYOffset || document.documentElement.scrollTop;
    window.scrollDirection = (window.st > window.lastScrollTop) ? "down" : "up";
    window.lastScrollTop = window.st <= 0 ? 0 : window.st;
}

function updateScrollProgress() {
    const scrollableHeight = window.dh - window.wh;
    window.scrollProgress = scrollableHeight > 0
        ? (document.documentElement.scrollTop / scrollableHeight) * 100
        : 0;
}

function doScrollActions() {
    // Scroll-to-top link fade in/out
    if (scrollNav) {
        if (window.lastScrollTop > SCROLL_TO_TOP_THRESHOLD) {
            fadeIn(scrollNav, window.fadeInSpeed);
        } else {
            fadeOut(scrollNav, window.fadeOutSpeed);
        }
    }

    // Update header/body scrolling classes
    body.classList.remove("down", "up");
    body.classList.add(window.scrollDirection);
    if (header) {
        header.classList.remove("down", "up");
        header.classList.add(window.scrollDirection);
    }

    // -- Smart sticky header --
    if (header) {
        const pastThreshold = window.lastScrollTop > STICKY_THRESHOLD;

        if (!pastThreshold) {
            // Near the top — header sits naturally, no transforms
            header.classList.remove('scrolled-hide', 'scrolled-show');
        } else if (window.scrollDirection === 'down') {
            // Scrolling down past threshold — slide header out of view
            header.classList.add('scrolled-hide');
            header.classList.remove('scrolled-show');
        } else {
            // Scrolling up past threshold — slide header back into view
            header.classList.remove('scrolled-hide');
            header.classList.add('scrolled-show');
        }
    }

    // "top" class
    if (window.lastScrollTop === 0) {
        if (!body.classList.contains("top")) {
            body.classList.add("top");
            if (header) header.classList.add("top");

            if (!isHome && typeof goToScribble === 'function') {
                goToScribble(1);
            }
        }
    } else {
        body.classList.remove("top");
        if (header) header.classList.remove("top");
    }

    // "bottom" class
    if (window.scrollProgress === 100) {
        if (header && !header.classList.contains("bottom") && typeof goToScribble === 'function') {
            goToScribble();
        }
        body.classList.add("bottom");
        if (header) header.classList.add("bottom");
    } else {
        body.classList.remove("bottom");
        if (header) header.classList.remove("bottom");
    }
}

// Throttled scroll handler
let scrollTimeout = null;

window.addEventListener("scroll", () => {
    if (scrollTimeout) return;

    scrollTimeout = setTimeout(() => {
        refreshScrollData();
        doScrollActions();
        scrollTimeout = null;
    }, SCROLL_THROTTLE_MS);
});

// Update dimensions on resize
let resizeTimeout = null;
window.addEventListener("resize", () => {
    if (resizeTimeout) return;

    resizeTimeout = setTimeout(() => {
        updateWindowDimensions();
        resizeTimeout = null;
    }, 100);
});


/////////////////////////////////////////////////////////////////////////////////////////////////////////
// FILTER FUNCTIONALITY
//////////////////////////////////////////////////////////////////////////////////////////////////////////
document.querySelectorAll(".filter-btn").forEach(btn => {
    btn.addEventListener("click", () => {
        const filter = btn.dataset.filter;

        // Remove active class from all filter buttons
        document.querySelectorAll(".filter-btn").forEach(b => {
            b.classList.remove("active");
        });

        // Add active class to clicked button
        btn.classList.add("active");

        // Filter cards
        document.querySelectorAll(".card-default").forEach(card => {
            const tags = card.dataset.tags ? card.dataset.tags.split(",") : [];

            const matches = filter === "all" || tags.includes(filter);

            if (matches) {
                card.style.display = "block";
                requestAnimationFrame(() => {
                    card.classList.remove("opacity-0");
                    card.classList.add("opacity-100");
                });
            } else {
                card.classList.remove("opacity-100");
                card.classList.add("opacity-0");

                setTimeout(() => {
                    card.style.display = "none";
                }, 300);
            }
        });
    });
});

document.addEventListener('click', (e) => {
    const clickable = e.target.closest('.clickable');
    if (!clickable) return;
    // Allow links within to work normally
    if (e.target.closest('a[href]')) return;
    let itemUrl = '';
    let target = '_self';
    if (clickable.dataset.url?.length) {
        // Priority 1: Use data-url if it exists
        itemUrl = clickable.dataset.url;
        target = clickable.dataset.target || target;
    } else {
        // Priority 2: Fall back to first link inside
        const firstLink = clickable.querySelector('a[href]');
        if (firstLink) {
            itemUrl = firstLink.getAttribute('href');
            target = firstLink.getAttribute('target') || target;
        }
    }

    if (itemUrl) {
        if (target === '_blank') {
            window.open(itemUrl, '_blank');
        } else {
            window.location.href = itemUrl;
        }
    }
});


////////////////////////////////////////////////////////////////////////////////////
// HAMBURGER / MOBILE NAV
////////////////////////////////////////////////////////////////////////////////////
(function () {
    const hamburger = document.querySelector('.js-open-mobile-menu');
    const siteHeader = document.querySelector('#header');

    if (!hamburger || !siteHeader) return;

    function openMenu() {
        hamburger.classList.add('open');
        siteHeader.classList.add('nav-open');
        hamburger.setAttribute('aria-expanded', 'true');
        hamburger.querySelector('.sr-only').textContent = 'Close Main Site Navigation';
        document.addEventListener('keydown', onEsc);
    }

    function closeMenu() {
        hamburger.classList.remove('open');
        siteHeader.classList.remove('nav-open');
        hamburger.setAttribute('aria-expanded', 'false');
        hamburger.querySelector('.sr-only').textContent = 'Open Main Site Navigation';
        document.removeEventListener('keydown', onEsc);
    }

    function toggleMenu() {
        hamburger.classList.contains('open') ? closeMenu() : openMenu();
    }

    // Close on Escape key
    function onEsc(e) {
        if (e.key === 'Escape') {
            closeMenu();
            hamburger.focus();
        }
    }

    // Close when clicking outside the header
    document.addEventListener('click', (e) => {
        if (siteHeader.classList.contains('nav-open') && !siteHeader.contains(e.target)) {
            closeMenu();
        }
    });

    // Close if viewport grows past the hamburger breakpoint (1280px)
    window.addEventListener('resize', () => {
        if (window.innerWidth > 1280 && siteHeader.classList.contains('nav-open')) {
            closeMenu();
        }
    });

    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-controls', 'header');
    hamburger.addEventListener('click', toggleMenu);
})();